import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import {
  Container,
  Box,
  TextField,
  Button,
  CssBaseline,
  Alert,
  CircularProgress,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Fade,
} from "@mui/material";
import darkTheme from "../../style/theme";
import { ThemeProvider } from "@mui/material";
import OpusLogo from "../../style/img/opus-logo.webp";
import CircularIndeterminate from "../../components/Loader.component";
import '../../index.css';
import { useActivity } from "../../contexts/ActivityTrackingContext";
import { Person, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import apiService from "../../services/api.service";

const Login = () => {
  const [userName, setuserName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const { trackActivity, ACTIVITY_TYPES, RESOURCE_TYPES } = useActivity();

  const fetchUserData = async (username) => {
    try {
      const data = await apiService.getUserData(username);
      
      localStorage.setItem("userData", JSON.stringify(data));
      return data;
    } catch (error) {
      console.error("Error fetching user data:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Call backend login API
      const data = await apiService.login({
        username: userName,
        password: password,
      });

      console.log("data as response from login => ", data)

      // Login successful
      if (data.data && data.data.accessToken) {
        // Store tokens in localStorage
        localStorage.setItem("accessToken", data.data.accessToken);
        localStorage.setItem("refreshToken", data.data.refreshToken);
        localStorage.setItem("idToken", data.data.idToken);
        localStorage.setItem("tokenExpiresIn", data.data.expiresIn);
        
        const { accessToken, refreshToken, idToken, expiresIn, ...userData } = data.data;
        localStorage.setItem("userData", JSON.stringify({"data":userData}));

        login();

        trackActivity(
          ACTIVITY_TYPES.LOGIN,
          RESOURCE_TYPES.USER,
          data.data._id,
          data.data.name || data.data.user_name || userName,
          {
            success: true,
            method: 'backend_api',
            userAgent: navigator.userAgent
          }
        );

        const returnTo = new URLSearchParams(window.location.search).get("returnTo");
        navigate(returnTo ? decodeURIComponent(returnTo) : "/dashboard/renderer-management");
      } else if (data.data && data.data.challengeName) {
        setError(`Authentication challenge required: ${data.data.challengeName}`);
        trackActivity(
          ACTIVITY_TYPES.LOGIN,
          RESOURCE_TYPES.USER,
          null,
          userName,
          {
            success: false,
            status: 'challenge_required',
            challenge: data.data.challengeName,
            method: 'backend_api',
            userAgent: navigator.userAgent
          }
        );
      }
    } catch (error) {
      console.error("Login error:", error);
      
      // Handle different error responses
      let errorMessage = "Login failed. Please try again.";
      
      if (error.message.includes("401")) {
        errorMessage = "Incorrect username or password.";
      } else if (error.message.includes("404")) {
        errorMessage = "User does not exist. Please sign up first.";
      } else if (error.message.includes("400")) {
        if (error.message.includes("not confirmed")) {
          errorMessage = "User not confirmed. Please check your email.";
        } else if (error.message.includes("password reset")) {
          errorMessage = "Password reset required. Please reset your password.";
        } else {
          errorMessage = error.message || errorMessage;
        }
      } else {
        errorMessage = error.message || "Network error. Please check your connection and try again.";
      }

      setError(errorMessage);
      
      trackActivity(
        ACTIVITY_TYPES.LOGIN,
        RESOURCE_TYPES.USER,
        null,
        userName,
        {
          success: false,
          error: error.message,
          error_message: errorMessage,
          method: 'backend_api',
          userAgent: navigator.userAgent
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />

      {/* Loading overlay */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
        >
          <CircularIndeterminate size={60} />
        </Box>
      )}

      {/* Main container with background gradient */}
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2c3e50 100%)',
          padding: 3,
        }}
      >
        <Paper
          elevation={10}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            maxWidth: '1000px',
            width: '100%',
            borderRadius: 3,
            overflow: 'hidden',
            background: 'rgba(50, 54, 56, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Left side - Logo and graphics */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 4,
            }}
          >
            <Fade in={true} timeout={1000}>
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={OpusLogo}
                  alt="Logo"
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    marginBottom: '2rem',
                  }}
                />
                <Typography variant="h4" sx={{ fontWeight: 300, color: '#fff', mb: 2 }}>
                  Welcome to OPUS
                </Typography>
                <Box sx={{
                  width: '100%',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  padding: 2,
                  backgroundColor: 'rgba(0, 0, 0, 0.2)'
                }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    A product by{' '}
                    <Typography
                      component="a"
                      href="https://ae.live"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: 'white',
                        textDecoration: 'none',
                        fontWeight: 'medium',
                        '&:hover': {
                          color: 'white',
                          textDecoration: 'underline',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      AE Live Group
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </Fade>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              padding: 4,
              justifyContent: 'center',
            }}
          >
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: 'white' }}>
              Sign In
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'rgba(255,255,255,0.7)' }}>
              Please enter your credentials to access your account
            </Typography>

            {/* Error message */}
            {error && (
              <Fade in={Boolean(error)}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      alignItems: 'center'
                    }
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            <Box
              component="form"
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              {/* Username field with icon */}
              <TextField
                label="Username"
                variant="outlined"
                fullWidth
                required
                value={userName}
                onChange={(e) => setuserName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: 'rgba(255,255,255,0.5)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.2)',
                      borderRadius: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(69, 255, 167, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#45FFA7',
                    },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 100px rgba(48, 54, 56, 1) inset !important',
                      WebkitTextFillColor: '#fff !important',
                      caretColor: '#fff !important',
                      borderRadius: 'inherit',
                      transition: 'background-color 5000s ease-in-out 0s',
                    },
                  },
                }}
              />

              {/* Password field with toggle visibility icon */}
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'rgba(255,255,255,0.5)' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff sx={{ color: 'rgba(255,255,255,0.5)' }} /> : <Visibility sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.2)',
                      borderRadius: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(69, 255, 167, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#45FFA7',
                    },
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 100px rgba(48, 54, 56, 1) inset !important',
                      WebkitTextFillColor: '#fff !important',
                      caretColor: '#fff !important',
                      borderRadius: 'inherit',
                      transition: 'background-color 5000s ease-in-out 0s',
                    },
                  },
                }}
              />

              {/* Sign in button */}
              <Button
                variant="outlined"
                fullWidth
                size="large"
                type="submit"
                disabled={loading}
                color="white"
                sx={{
                  mt: 2,
                  py: 1.5,
                  color: "#fff",
                  fontWeight: "bold",
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  border: "1px solid #fff",
                  '&:hover': {
                    backgroundColor: "white",
                    color: "#000",
                    boxShadow: '0 6px 25px rgba(69, 255, 167, 0.35)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={24} sx={{ color: loading ? '#000' : '#fff', mr: 1 }} />
                    <Typography>Signing in...</Typography>
                  </Box>
                ) : "Sign in"}
              </Button>

              {/* Forgot password link */}
              <Box sx={{ textAlign: 'center', mt: 2, color: 'white' }}>
                <Typography
                  component="a"
                  href={'/reset-password?username=' + userName}
                  sx={{
                    color: 'white',
                    textDecoration: 'none',
                    fontWeight: 'medium',
                    cursor: userName.length > 2 ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'white',
                      textDecoration: userName.length > 2 ? 'underline' : 'none',
                    },
                    '&:visited': {
                      color: 'white',
                    },
                    '&:active': {
                      color: 'white',
                    },
                  }}
                >
                  Forgot your password?
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

export default Login;